import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Form, ResponsiveContext } from 'grommet';
import { CircleInformation } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService, FacilityService, TCreateFacilityRequest } from '/src/api';
import {
  AddressAutocompleteInput,
  AddressAutocompletePlaceResult,
  Box,
  BreadcrumbNav,
  ContactsSelect,
  FirstActivePeriodSelect,
  FormCard,
  FormCardSection,
  FormPage,
  Input,
  Line,
  Select,
  UtilitySelect,
} from '/src/components';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { Client, ClientReportingPeriod, TClientProgramId, TRegionId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { CountryId, DateString, FacilityDataForm, FormMetadata, SelectOptions } from '/src/lib/types';
import { getFormErrors, getIsMobile, getPageTitle } from '/src/utils';

export const CreateFacilityPage = observer(() => {
  /** Context **/
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const screenSize = useContext(ResponsiveContext);
  const params = useParams();
  const { t } = useTranslation();

  /** Refs **/
  const defaultFormData = useRef<FacilityDataForm>({
    name: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_region_id: null,
    address_post_code: '',
    utility_id: null,
    program_id: null,
    first_active_reporting_quarter: '',
    last_active_reporting_quarter: null,
    comments: '',
  });

  /** State **/
  const [isFetchingPrograms, setIsFetchingPrograms] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [client, setClient] = useState<Client>();
  const [clientProgramOptions, setClientProgramOptions] = useState<SelectOptions<TClientProgramId>>();

  const [formValues, setFormValues] = useState<FacilityDataForm>(defaultFormData.current);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /** Computed **/
  const clientId = parseInt(params.client_id ?? '');
  const isMobile = getIsMobile(screenSize);
  const isCanada = client?.country_id === CountryId.Canada;
  const isTercProgram = formValues.program_id === 6;
  const regionType = isCanada ? 'province' : 'state';
  const programEmptyMessage = !formValues.address_region_id
    ? t('facility:chooseProgramHelp', { regionType })
    : t('facility:noPrograms', { regionType });

  const regionOptions = useMemo(() => {
    if (client?.country_id && globalStore.regions) {
      return globalStore.regions
        .filter((r) => r.short_code && r.country_id === client.country_id)
        .map((region) => ({
          label: `${region.name} (${region.short_code})`,
          value: region.id,
        }));
    }
    return [];
  }, [client?.country_id, globalStore.regions]);

  const programName = useMemo(
    () => (formValues.program_id ? clientProgramOptions?.find((o) => o.value === formValues.program_id)?.label : ''),
    [formValues.program_id, clientProgramOptions]
  );

  const isLoading = !client;

  /** Methods **/
  const updateFormValue = (key: string, value: ClientReportingPeriod | string | number | DateString | null) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: key === 'program_id' ? Number(value) : value,
    }));
    if (formErrors[key]) formErrors[key] = '';
  };

  const handlePlaceSelected = (place: AddressAutocompletePlaceResult) => {
    if (place.line2) updateFormValue('address_line2', place.line2);
    if (place.city) updateFormValue('address_city', place.city);
    if (place.postCode) updateFormValue('address_post_code', place.postCode);
    if (place.stateShortCode) {
      const region = globalStore.regions.find(
        (r) => r.short_code?.toLowerCase() === place.stateShortCode.toLowerCase()
      );
      if (region) updateFormValue('address_region_id', region.id);
    }
  };

  /**
   * Fetches page data.
   */
  const fetchPageData = async () => {
    try {
      const client =
        userStore.isClientUser || userStore.isFacilityUser
          ? userStore.user?.clients.find((client) => client.id === clientId)
          : await ClientService.get({ id: clientId });

      if (client) {
        setClient(client);
      }
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchClient.error);
    }
  };

  /**
   * Creates a Facility with the current form values.
   * @returns
   */
  const createFacility = async () => {
    if (!client?.name || isCreating || isFetchingPrograms) {
      toast.error(toastMessages.formValidation.requiredFields);
      return;
    }

    const formFields: FormMetadata = {
      name: { label: t('common:name'), max: 50 },
      address_line1: { label: t('common:address'), max: 100, required: true },
      address_line2: { label: t('common:addressContinued'), max: 100 },
      address_city: { label: t('common:city'), max: 50, required: true },
      address_region_id: { label: t('common:stateProvince'), required: true },
      address_post_code: { label: t('common:postalCode'), max: 20, required: true },
      program_id: { label: t('common:program'), required: true },
      utility_id: { label: t('facility:utility'), required: !isCanada && !isTercProgram },
      first_active_reporting_quarter: { label: t('common:firstReportingPeriod'), required: true },
      comments: { label: t('common:comments'), max: 500 },
    };

    const errors = getFormErrors(formFields, formValues);
    if (errors) {
      setFormErrors(errors);
      toast.error(toastMessages.formValidation.requiredFields);
      return;
    }

    const newFacility: TCreateFacilityRequest = {
      client_id: clientId,
      name: formValues.name || null,
      address_line1: formValues.address_line1,
      address_line2: formValues.address_line2 || null,
      address_city: formValues.address_city,
      address_region_id: formValues.address_region_id ?? 0,
      address_post_code: formValues.address_post_code,
      program_id: formValues.program_id,
      utility_id: isCanada ? null : formValues.utility_id,
      first_active_reporting_quarter: formValues.first_active_reporting_quarter ?? '',
      comments: formValues.comments || null,
    };

    try {
      setIsCreating(true);
      const facility = await FacilityService.create(newFacility);
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(toastMessages.createFacility.success);
      navigate(`/clients/${clientId}/facilities/${facility.id}`);
    } catch (err) {
      const error = err as ApiError;
      const errorBody = error.body as { message?: string; errors?: { rule?: string; field?: string }[] } | undefined;

      if (errorBody?.message?.toLowerCase() === 'facility already exists') {
        globalStore.handleApiError(error, () =>
          toastMessages.duplicateFacility.error({
            ...error,
            body: { ...errorBody, program_name: programName },
          })
        );
      } else if (errorBody?.errors?.some((e) => e.rule === 'unique' && e.field === 'name')) {
        globalStore.handleApiError(error, toastMessages.duplicateFacilityName.error);
      } else {
        globalStore.handleApiError(error, toastMessages.createFacility.error);
      }
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Fetches a filtered list of available Programs.
   * @param client
   * @param regionId
   * @param startPeriod
   */
  const fetchPrograms = async (client: Client, regionId: TRegionId, startPeriod?: DateString) => {
    try {
      setIsFetchingPrograms(true);

      type ListClientProgramsRequest = {
        id: number;
        program_region_id: TRegionId;
        reporting_period_type_id?: number;
        start_reporting_quarter?: DateString;
        end_reporting_quarter?: DateString;
      };

      const request: ListClientProgramsRequest = {
        id: client.id,
        program_region_id: regionId,
      };

      if (startPeriod) {
        request.reporting_period_type_id = client.reporting_period_type_id;
        request.start_reporting_quarter = startPeriod;
        request.end_reporting_quarter = startPeriod;
      }

      const response = await ClientService.listClientPrograms(request);

      const uniquePrograms = new Map();
      response.forEach((program) => {
        if (!uniquePrograms.has(program.program_id)) {
          uniquePrograms.set(program.program_id, {
            label: program.program.name,
            value: program.program_id,
            rate: program.percent_rate || program.fixed_kwh_rate,
          });
        }
      });

      const options = Array.from(uniquePrograms.values());
      setClientProgramOptions(options);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listPrograms.error);
    } finally {
      setIsFetchingPrograms(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, t('facility:addFacility'));
  }, [t]);

  useEffect(() => {
    if (isLoading) {
      fetchPageData();
    }
  }, [isLoading]);

  useEffect(() => {
    if (client && formValues.address_region_id) {
      fetchPrograms(client, formValues.address_region_id, formValues.first_active_reporting_quarter);
    }
  }, [client, formValues.address_region_id]);

  useEffect(() => {
    if (client && formValues.first_active_reporting_quarter && formValues.address_region_id) {
      const currentProgramId = formValues.program_id;

      fetchPrograms(client, formValues.address_region_id, formValues.first_active_reporting_quarter).then(() => {
        if (currentProgramId) {
          updateFormValue('program_id', currentProgramId);
        }
      });
    }
  }, [formValues.first_active_reporting_quarter]);

  /** Render **/
  return (
    <FormPage
      title={t('facility:addFacility')}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            {
              name: t('navigation:clients'),
              link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined,
            },
            { name: client?.name ?? '', link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
            { name: t('navigation:facilities'), link: `/clients/${clientId}/facilities` },
          ]}
          currentPageName={t('facility:addFacility')}
        />
      }
      isLoading={isLoading}
    >
      <FormCard
        title={t('common:generalInfo')}
        icon={<CircleInformation size="24px" color="brand" />}
        isLoading={isLoading || isCreating}
        onSubmit={createFacility}
      >
        <Form>
          <Box direction={isMobile ? 'column' : 'row'} margin={{ bottom: '1rem' }} gap="1rem">
            <Box width={isMobile ? '100%' : '60%'} gap="1rem">
              <FormCardSection title={t('facility:facilityInfo')}>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="facility_name"
                    label={t('common:name')}
                    flex
                    value={formValues.name ?? ''}
                    setValue={(value) => updateFormValue('name', value)}
                    error={formErrors['name']}
                    onSubmit={createFacility}
                  />
                  <FirstActivePeriodSelect
                    id="first_active_reporting_quarter"
                    label={t('common:firstReportingPeriod')}
                    placeholder={t('common:selectPeriod')}
                    value={formValues.first_active_reporting_quarter ?? ''}
                    setValue={(value) => updateFormValue('first_active_reporting_quarter', value)}
                    clientId={client?.id}
                    programId={formValues.program_id ?? undefined}
                    reportingPeriodTypeId={client?.reporting_period_type_id}
                    firstActivePeriod={client?.first_active_reporting_quarter}
                    error={formErrors['first_active_reporting_quarter']}
                    required
                    flex
                  />
                </Box>
                <AddressAutocompleteInput
                  name="address_line1"
                  label={t('common:address')}
                  value={formValues.address_line1}
                  setValue={(value) => updateFormValue('address_line1', value)}
                  onPlaceSelected={handlePlaceSelected}
                  error={formErrors['address_line1']}
                  onSubmit={createFacility}
                  required
                />
                <Input
                  id="address_line2"
                  autoComplete="address_line2"
                  label={t('common:addressContinued')}
                  value={formValues.address_line2 ?? ''}
                  setValue={(value) => updateFormValue('address_line2', value)}
                  error={formErrors['address_line2']}
                  onSubmit={createFacility}
                />
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="address_city"
                    label={t('common:city')}
                    value={formValues.address_city}
                    setValue={(value) => updateFormValue('address_city', value)}
                    error={formErrors['address_city']}
                    onSubmit={createFacility}
                    flex
                    required
                  />
                  <Select
                    id="address_region_id"
                    label={t('common:stateProvince')}
                    value={formValues.address_region_id}
                    setValue={(value) => updateFormValue('address_region_id', value)}
                    options={regionOptions}
                    error={formErrors['address_region_id']}
                    required
                    flex
                  />
                  <Input
                    id="address_post_code"
                    label={t('common:postalCode')}
                    value={formValues.address_post_code}
                    setValue={(value) => updateFormValue('address_post_code', value)}
                    error={formErrors['address_post_code']}
                    onSubmit={createFacility}
                    flex
                    required
                  />
                </Box>
              </FormCardSection>
              <Line margin="0.5rem" />
              <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                <Select
                  id="program"
                  name="program"
                  label={t('common:program')}
                  placeholder={t('facility:chooseProgram')}
                  emptySearchMessage={programEmptyMessage}
                  value={formValues.program_id}
                  setValue={(value) => updateFormValue('program_id', value)}
                  error={formErrors['program_id']}
                  options={clientProgramOptions ?? []}
                  required
                  flex
                />
                {!isCanada && !isTercProgram && (
                  <UtilitySelect
                    id="utility"
                    label={t('facility:utility')}
                    placeholder={t('facility:chooseUtility')}
                    emptySearchMessage={
                      !formValues.address_region_id ? t('facility:chooseUtilityHelp') : t('facility:noUtilities')
                    }
                    value={formValues.utility_id}
                    setValue={(value) => updateFormValue('utility_id', value)}
                    error={formErrors['utility_id']}
                    regionId={formValues.address_region_id}
                    required={!isCanada && !isTercProgram}
                  />
                )}
              </Box>
              <Line margin="0.5rem" />
              <Box flex="grow">
                <Input
                  id="comments"
                  label={t('common:comments')}
                  value={formValues.comments ?? ''}
                  setValue={(value) => updateFormValue('comments', value)}
                  error={formErrors['comments']}
                  onSubmit={createFacility}
                  componentType="textArea"
                  centerLabel={false}
                  height="12rem"
                  disabled={!userStore.isAdminUser}
                />
              </Box>
            </Box>
            {isMobile && <Line margin="1rem" />}
            {!isMobile && <Line direction="vertical" margin="3rem" />}
            <Box width={isMobile ? '100%' : '40%'}>
              <ContactsSelect isFacility />
            </Box>
          </Box>
        </Form>
      </FormCard>
    </FormPage>
  );
});
